import React, {useContext, useState} from "react";
import {DialogContent, DialogContentText, DialogTitle} from "@mui/material";
import {useAsyncAction} from "nate-react-api-helpers";
import {Dialog2} from "../../../../misc/Dialog2";
import {SmoothActionBar} from "../../../../misc/SmoothActionBar";
import {useTableData} from "../../../../misc/scroller/Table";
import {PrintManagerContext} from "../../../../misc/pdf/PrintManager";
import {api} from "../../../../api/API";
import {Opening} from "../../../../api/Openings";
import {QuoteOpening} from "../../../../api/QuoteOpenings";
import {RenderingStatus} from "../../../../api/ElevationTool";
import {useProject, useProjectId} from "../ProjectName";
import {useIsQuote} from "../../quote/QuoteContainer";
import {useAlternative} from "../../quote/alternative/Alternative";
import {currentTab, openingsTableName} from "../openings/Openings";
import {currentQuoteTab, quoteOpeningsTableName} from "../../quote/openings/QuoteOpenings";
import {SubmittalRenderGate} from "../openings/SubmittalRenderGate";
import {getFrameCutPlan, NO_CUT_PLANS_ERROR} from "./FrameCutPlanPDF";

// generate the frame cut plan for the openings in the grid: one page per screen elevation variant,
// with the labelled drawing and the stick table. The sticks come off the elevation renderings, so
// generating is gated on those being up to date (see SubmittalRenderGate).
export function FrameCutPlan(props: {
    onDone(): void;
}) {
    const projectId = useProjectId();
    const project = useProject();
    const isQuote = useIsQuote();
    const alternative = useAlternative();
    const printCtx = useContext(PrintManagerContext);

    const gridOpenings = useTableData(isQuote
        ? quoteOpeningsTableName(projectId, currentQuoteTab)
        : openingsTableName(projectId, currentTab)) as (Opening|QuoteOpening)[];

    const openingIds = gridOpenings.map(o => o.id);

    const [renderGate, setRenderGate] = useState<RenderingStatus | null>(null);

    const doGenerate = async () => {
        if(!project) return;

        const groups = await api.elevationTool.frameCutPlans({
            project: project.id,
            alternative: isQuote ? alternative : undefined,
            openings: openingIds,
        });
        if(groups.length === 0) throw new Error(NO_CUT_PLANS_ERROR);

        await printCtx.print2(getFrameCutPlan({project, groups}));

        props.onDone();
    };

    const submit = useAsyncAction(async () => {
        if(!project) return;

        // re-render (in place) any rendering left by an older render version first, so every
        // variant carries its cut plan.
        await api.elevationTool.refreshRenderings({project: project.id});

        const status = await api.elevationTool.renderingStatus({project: project.id});
        if(status.pending > 0 || status.failed > 0) {
            setRenderGate(status);
            return;
        }

        await doGenerate();
    }, [project, doGenerate]);

    // generate after the user chose to proceed past pending/failed renders
    const proceed = useAsyncAction(async () => {
        await doGenerate();
    }, [doGenerate]);

    return (
        <>
            <Dialog2 open maxWidth="sm" fullWidth onClose={props.onDone}>
                <DialogTitle>Create Frame Cut Plan</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        One page for each screen elevation variant of the {openingIds.length} opening(s) in the grid.
                        Openings without a drawn screen elevation are left out.
                    </DialogContentText>
                </DialogContent>
                <SmoothActionBar
                    loading={submit.loading || proceed.loading}
                    error={submit.error || proceed.error}
                    onCancel={props.onDone}
                    onSubmit={() => submit.callback(undefined)}
                    actionName="Create and Download"
                    submittingWord="Creating…"
                />
            </Dialog2>
            {renderGate && project && <SubmittalRenderGate
                projectId={project.id}
                status={renderGate}
                onProceed={() => {
                    setRenderGate(null);
                    proceed.callback(undefined);
                }}
                onCancel={() => setRenderGate(null)}
            />}
        </>
    );
}
